/**
 * UI store - UI state management.
 *
 * @module stores/ui
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

/**
 * Toast notification.
 */
export interface Toast {
  id: number
  message: string
  type: 'info' | 'success' | 'warning' | 'error'
  duration: number
}

/**
 * UI store.
 */
export const useUiStore = defineStore('ui', () => {
  // State
  const toasts = ref<Toast[]>([])
  const themeMode = ref<'auto' | 'light' | 'dark'>('auto')
  const sidebarOpen = ref(true)
  const modalOpen = ref(false)
  const modalContent = ref<string | null>(null)

  // Toast counter for unique IDs
  let toastId = 0

  // Getters
  const hasToasts = computed(() => toasts.value.length > 0)
  const isAuto = computed(() => themeMode.value === 'auto')
  const isForced = computed(() => themeMode.value !== 'auto')
  const isDark = computed(() => {
    if (themeMode.value === 'dark') return true
    if (themeMode.value === 'light') return false
    // Auto: follow system preference
    return window.matchMedia('(prefers-color-scheme: dark)').matches
  })

  // Actions
  /**
   * Show a toast notification.
   *
   * @param message - Toast message
   * @param type - Toast type
   * @param duration - Duration in ms (0 = no auto-dismiss)
   * @returns Toast ID
   */
  function showToast(
    message: string,
    type: 'info' | 'success' | 'warning' | 'error' = 'info',
    duration = 5000
  ): number {
    const id = ++toastId
    const toast: Toast = { id, message, type, duration }
    toasts.value.push(toast)

    if (duration > 0) {
      setTimeout(() => {
        dismissToast(id)
      }, duration)
    }

    // Completed
    return id
  }

  /**
   * Dismiss a toast.
   *
   * @param id - Toast ID
   */
  function dismissToast(id: number): void {
    const index = toasts.value.findIndex((t) => t.id === id)
    if (index !== -1) {
      toasts.value.splice(index, 1)
    }
    // Completed
  }

  /**
   * Clear all toasts.
   */
  function clearToasts(): void {
    toasts.value = []
    // Completed
  }

  /**
   * Apply theme to document.
   * Matches theme_v1.js: sets data-theme and data-theme-mode attributes.
   */
  function applyTheme(): void {
    const effectiveTheme = isDark.value ? 'dark' : 'light'
    document.documentElement.setAttribute('data-theme', effectiveTheme)
    document.documentElement.setAttribute('data-theme-mode', themeMode.value === 'auto' ? 'auto' : 'forced')
    // Completed
  }

  /**
   * Toggle theme mode: auto -> light -> dark -> auto.
   */
  function toggleTheme(): void {
    const modes = ['auto', 'light', 'dark'] as const
    const currentIndex = modes.indexOf(themeMode.value)
    const nextIndex = (currentIndex + 1) % 3
    themeMode.value = modes[nextIndex] as 'auto' | 'light' | 'dark'
    localStorage.setItem('org.cylian.settings.theme', themeMode.value)
    applyTheme()
    // Completed
  }

  /**
   * Set theme mode.
   *
   * @param mode - Theme mode to set
   */
  function setTheme(mode: 'auto' | 'light' | 'dark'): void {
    themeMode.value = mode
    localStorage.setItem('org.cylian.settings.theme', mode)
    applyTheme()
    // Completed
  }

  /**
   * Initialize theme from localStorage.
   */
  function initTheme(): void {
    const stored = localStorage.getItem('org.cylian.settings.theme')
    if (stored === 'auto' || stored === 'light' || stored === 'dark') {
      themeMode.value = stored
    }
    applyTheme()

    // Listen for system theme changes in auto mode
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
      if (themeMode.value === 'auto') {
        applyTheme()
      }
    })
    // Completed
  }

  /**
   * Toggle sidebar.
   */
  function toggleSidebar(): void {
    sidebarOpen.value = !sidebarOpen.value
    // Completed
  }

  /**
   * Open modal.
   *
   * @param content - Modal content identifier
   */
  function openModal(content: string): void {
    modalContent.value = content
    modalOpen.value = true
    // Completed
  }

  /**
   * Close modal.
   */
  function closeModal(): void {
    modalOpen.value = false
    modalContent.value = null
    // Completed
  }

  // Completed
  return {
    // State
    toasts,
    themeMode,
    sidebarOpen,
    modalOpen,
    modalContent,
    // Getters
    hasToasts,
    isAuto,
    isForced,
    isDark,
    // Actions
    showToast,
    dismissToast,
    clearToasts,
    toggleTheme,
    setTheme,
    initTheme,
    toggleSidebar,
    openModal,
    closeModal,
  }
})

// Completed
